import gsap from 'gsap';

import { LitElement, css, html } from 'lit';

import { createCustomAwaitable } from '@/util/core/createCustomAwaitable';
import { createDelegateFunction } from '@/util/core/createDelegateFunction';
import { trackOrientationAsCSSClass } from '@/helpers/trackOrientationAsCSSClass';

const TAG_NAME = 'share-success-splash-screen';

class ShareSuccessSplash extends LitElement {
  public static readonly styles = css`
    :host {
      --splash-screen-padding: 0vmin;

      position: absolute;
      top: var(--splash-screen-padding);
      left: var(--splash-screen-padding);
      right: var(--splash-screen-padding);
      bottom: var(--splash-screen-padding);

      display: flex;
      justify-content: center;
      align-items: center;

      font-family: 'Helvetica Neue', sans-serif;

      background: rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(10px);

      zoom: 0.7;
    }

    .container {
      box-sizing: border-box;

      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      padding: 3em;
      text-align: center;
      color: white;

      max-height: 80%;
    }

    .title {
      font-size: 3.7em;
      text-shadow: 0 2px 1rem #0009;
      margin-bottom: 0.5rem;

      font-family: 'Lora', serif;
      font-optical-sizing: auto;
      font-weight: 400;
      font-style: normal;

      transition: font-size 0.2s;
    }

    .subtitle {
      font-size: 1.6em;
      text-shadow: 0 2px 1rem #0009;
      margin-top: 1rem;
      margin-bottom: 1.4rem;

      font-family: 'Inter', sans-serif;
      font-optical-sizing: auto;
      font-weight: 300;
      font-style: normal;

      color: #cdcdcd;
    }

    .container .continue-button {
      margin-top: 3rem;
      padding: 0.75em 1.25em;
      background-color: rgba(0, 0, 0, 0.4);
      color: white;
      border: none;
      border-radius: 250px;
      cursor: pointer;
      transition:
        color 0.2s,
        padding 0.2s;

      font-family: Inter;
      font-size: 32px;
      font-weight: 200;
      line-height: 24px;
      text-align: left;
      margin-bottom: 3rem;
    }

    .container .continue-button:hover {
      color: cyan;
      padding: 0.75em 1.5em;
    }

    :host(.portrait) {
      .title {
        font-size: 2.2rem;
      }
      .subtitle {
        font-size: 0.9rem;
      }
    }
  `;

  public readonly onBegin = createDelegateFunction();

  public userName = '';

  render() {
    return html`
      <div class="container">
        <div class="content">
          <!-- <wip-disclaimer>This screen is a work in progress.</wip-disclaimer> -->
          <div class="title">Thanks for paying it forward, ${this.userName}!</div>
          <div class="subtitle">Presence Shift 1 is ready for you.</div>
        </div>
        <button id="continue-button-share-screen" class="continue-button" @click=${this.onBegin}>Begin ❯</button>
      </div>
    `;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.onBegin.clear();
  }
}

customElements.define(TAG_NAME, ShareSuccessSplash);

export async function displayShareSuccessSplash(parentElement: HTMLDivElement, userName: string) {
  const awaitable = createCustomAwaitable<void>();

  const splashScreen = new ShareSuccessSplash();
  splashScreen.onBegin.on(async () => awaitable.resolve());
  splashScreen.userName = userName || 'friend';
  parentElement.appendChild(splashScreen);
  trackOrientationAsCSSClass(splashScreen);

  await gsap.from(splashScreen, { duration: 0.5, opacity: 0 });

  await awaitable;

  await gsap.to(splashScreen, { duration: 0.5, opacity: 0 });
  parentElement.removeChild(splashScreen);
}
